import { compact, getSlotCompoundVariant, memo, splitProps } from '../helpers.mjs';
import { createRecipe } from './create-recipe.mjs';

const sidebarDefaultVariants = {
  "variant": "sidebar",
  "side": "left",
  "collapsible": "offcanvas",
  "state": "expanded"
}
const sidebarCompoundVariants = [
  {
    "collapsible": "offcanvas",
    "css": {
      "gap": {
        "width": "0"
      }
    }
  },
  {
    "collapsible": "icon",
    "variant": "sidebar",
    "css": {
      "gap": {
        "width": "var(--sidebar-width-icon)"
      },
      "container": {
        "width": "var(--sidebar-width-icon)"
      }
    }
  },
  {
    "collapsible": "icon",
    "variant": "floating",
    "css": {
      "gap": {
        "width": "calc(var(--sidebar-width-icon) + token(spacing.4))"
      },
      "container": {
        "width": "calc(var(--sidebar-width-icon) + token(spacing.4) + 2px)"
      }
    }
  },
  {
    "collapsible": "icon",
    "variant": "inset",
    "css": {
      "gap": {
        "width": "calc(var(--sidebar-width-icon) + token(spacing.4))"
      },
      "container": {
        "width": "calc(var(--sidebar-width-icon) + token(spacing.4) + 2px)"
      }
    }
  },
  {
    "collapsible": "offcanvas",
    "side": "left",
    "state": "collapsed",
    "css": {
      "container": {
        "left": "calc(var(--sidebar-width) * -1)"
      }
    }
  },
  {
    "collapsible": "offcanvas",
    "side": "right",
    "state": "collapsed",
    "css": {
      "container": {
        "right": "calc(var(--sidebar-width) * -1)"
      }
    }
  },
  {
    "variant": "inset",
    "state": "collapsed",
    "css": {
      "inset": {
        "md": {
          "marginLeft": "0.5rem"
        }
      }
    }
  },
  {
    "collapsible": "offcanvas",
    "state": "expanded",
    "css": {
      "gap": {
        "width": "var(--sidebar-width)"
      },
      "container": {
        "left": "0"
      }
    }
  },
  {
    "collapsible": "offcanvas",
    "state": "expanded",
    "side": "right",
    "css": {
      "container": {
        "right": "0",
        "left": "auto"
      }
    }
  },
  {
    "collapsible": "icon",
    "state": "expanded",
    "css": {
      "gap": {
        "width": "var(--sidebar-width)"
      },
      "container": {
        "width": "var(--sidebar-width)"
      }
    }
  },
  {
    "collapsible": "icon",
    "state": "collapsed",
    "css": {
      "header": {
        "p": "9px 9.5px 9px 9.5px",
        "gap": "12"
      },
      "logo": {},
      "group": {
        "px": "7.5"
      },
      "groupLabel": {
        "marginTop": "-32",
        "opacity": "0"
      },
      "menuButton": {
        "_hover": {},
        "& svg": {}
      }
    }
  }
]

const sidebarSlotNames = [
  [
    "sidebarAndBodyWrapper",
    "sidebar__sidebarAndBodyWrapper"
  ],
  [
    "sidebar",
    "sidebar__sidebar"
  ],
  [
    "root",
    "sidebar__root"
  ],
  [
    "logo",
    "sidebar__logo"
  ],
  [
    "gap",
    "sidebar__gap"
  ],
  [
    "container",
    "sidebar__container"
  ],
  [
    "inner",
    "sidebar__inner"
  ],
  [
    "trigger",
    "sidebar__trigger"
  ],
  [
    "rail",
    "sidebar__rail"
  ],
  [
    "inset",
    "sidebar__inset"
  ],
  [
    "input",
    "sidebar__input"
  ],
  [
    "header",
    "sidebar__header"
  ],
  [
    "footer",
    "sidebar__footer"
  ],
  [
    "separator",
    "sidebar__separator"
  ],
  [
    "content",
    "sidebar__content"
  ],
  [
    "group",
    "sidebar__group"
  ],
  [
    "groupLabel",
    "sidebar__groupLabel"
  ],
  [
    "groupAction",
    "sidebar__groupAction"
  ],
  [
    "groupContent",
    "sidebar__groupContent"
  ],
  [
    "menu",
    "sidebar__menu"
  ],
  [
    "menuItem",
    "sidebar__menuItem"
  ],
  [
    "menuButton",
    "sidebar__menuButton"
  ],
  [
    "menuAction",
    "sidebar__menuAction"
  ],
  [
    "menuSub",
    "sidebar__menuSub"
  ],
  [
    "menuSubItem",
    "sidebar__menuSubItem"
  ],
  [
    "menuSubButton",
    "sidebar__menuSubButton"
  ],
  [
    "menuSkeleton",
    "sidebar__menuSkeleton"
  ],
  [
    "menuSkeletonIcon",
    "sidebar__menuSkeletonIcon"
  ],
  [
    "menuSkeletonText",
    "sidebar__menuSkeletonText"
  ],
  [
    "menuBadge",
    "sidebar__menuBadge"
  ]
]
const sidebarSlotFns = /* @__PURE__ */ sidebarSlotNames.map(([slotName, slotKey]) => [slotName, createRecipe(slotKey, sidebarDefaultVariants, getSlotCompoundVariant(sidebarCompoundVariants, slotName))])

const sidebarFn = memo((props = {}) => {
  return Object.fromEntries(sidebarSlotFns.map(([slotName, slotFn]) => [slotName, slotFn.recipeFn(props)]))
})

const sidebarVariantKeys = [
  "variant",
  "side",
  "state",
  "collapsible"
]
const getVariantProps = (variants) => ({ ...sidebarDefaultVariants, ...compact(variants) })

export const sidebar = /* @__PURE__ */ Object.assign(sidebarFn, {
  __recipe__: false,
  __name__: 'sidebar',
  raw: (props) => props,
  classNameMap: {},
  variantKeys: sidebarVariantKeys,
  variantMap: {
  "variant": [
    "sidebar",
    "floating",
    "inset"
  ],
  "side": [
    "left",
    "right"
  ],
  "state": [
    "expanded",
    "collapsed"
  ],
  "collapsible": [
    "offcanvas",
    "icon",
    "none"
  ]
},
  splitVariantProps(props) {
    return splitProps(props, sidebarVariantKeys)
  },
  getVariantProps
})